import { type ReactElement } from 'react';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildTestDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { buildTableV2NestedBodyRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2TagGroup,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { TableCell, TableRow } from '@/ui/primitives';
import { getSummaryName } from './ExecutionRunsTableV2NestedSorting';
import { StatusChip } from './ExecutionRunsTableV2RowShared';
import type { TagColor } from '@/shared/data/tags';
import type { ExecutionRunDto, TestRunDto, TestRunSummaryDto } from '@flowqa/shared';

const resolveSummaryTimestamp = (
  timestamp: Date | null | undefined,
  parent: ExecutionRunDto,
): Date =>
  timestamp ?? parent.startedAt ?? parent.finishedAt ?? parent.resultsFileCreatedAt ?? new Date(0);

const buildTestRunFromSummary = ({
  parent,
  suiteRunId,
  suiteSlug,
  summary,
}: {
  parent: ExecutionRunDto;
  suiteRunId: string | null;
  suiteSlug: string | null;
  summary: TestRunSummaryDto;
}): TestRunDto | null => {
  if (!summary.executionId || !suiteRunId) return null;
  const timestamp = resolveSummaryTimestamp(summary.timestamp, parent);
  return {
    id: summary.executionId,
    runId: parent.id,
    resultsFileId: parent.resultsFileId ?? null,
    suiteRunId,
    testId: summary.testId,
    runNumber: summary.runNumber ?? null,
    testName: summary.testName ?? null,
    displayName: summary.displayName ?? null,
    groupPath: summary.groupPath,
    testSlug: summary.testSlug ?? null,
    tags: summary.tags ?? [],
    suiteName: summary.suiteName ?? null,
    suiteSlug,
    projectId: parent.projectId,
    projectName: parent.projectName ?? null,
    projectSlug: parent.projectSlug ?? null,
    organisationId: parent.organisationId,
    status: summary.status ?? 'untested',
    timestamp,
    durationMs: summary.durationMs ?? null,
    createdAt: parent.resultsFileCreatedAt ?? timestamp,
    deletedAt: null,
  };
};

export const TestRunDetailRow = ({
  deleteRowPendingId,
  editRowPendingId,
  groupErroredWithFailed,
  parent,
  suiteRunId,
  suiteSlug,
  tagColorByName,
  test,
  onDelete,
  onEdit,
}: {
  deleteRowPendingId: string | null;
  editRowPendingId: string | null;
  groupErroredWithFailed: boolean;
  parent: ExecutionRunDto;
  suiteRunId: string | null;
  suiteSlug: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  test: TestRunSummaryDto;
  onDelete: (row: TestRunDto) => void;
  onEdit: (row: TestRunDto) => void;
}): ReactElement => {
  const testName = getSummaryName(test);
  const testRunNumber = test.runNumber ?? null;
  const testRunNumberLabel = formatTableV2RunNumber(testRunNumber);
  const testHref = buildTestDetailPath(test.testSlug ?? test.testId);
  const testRunHref =
    test.testSlug && testRunNumber !== null
      ? `/tests/${test.testSlug}/runs/${testRunNumber}`
      : null;
  const ranAt = test.timestamp ?? null;
  const tags = resolveEntityTags(test);
  const testRun = buildTestRunFromSummary({ parent, suiteRunId, suiteSlug, summary: test });
  const displayStatus = resolveProjectDisplayStatus(
    test.status ?? 'untested',
    groupErroredWithFailed,
  );
  const displayStatusLabel = resolveProjectDisplayStatusLabel(
    test.status ?? 'untested',
    groupErroredWithFailed,
  );

  return (
    <TableRow
      sx={[
        buildTableV2NestedBodyRowSx({ depth: 2, hasRenderedDivider: true }),
        {
          '& > td': {
            boxSizing: 'border-box',
            maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
            overflow: 'hidden',
          },
        },
      ]}
    >
      <TableCell>
        <TableV2NameCell
          href={testHref}
          primary={testName}
          secondary={testRunNumberLabel}
          secondaryHref={testRunHref}
        />
      </TableCell>
      <TableCell>
        <StatusChip status={displayStatus} label={displayStatusLabel} />
      </TableCell>
      <TableCell>
        <TableV2DurationCell value={test.durationMs ?? null} />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={ranAt} />
      </TableCell>
      <TableCell>
        <TableV2TagGroup tagColorByName={tagColorByName} tags={tags} />
      </TableCell>
      <TableCell align="center">
        <TableActionsCell
          rowName={testName}
          verticalAlign="center"
          disabledTooltip={testRun ? null : 'Actions unavailable for this test run.'}
          editActionLabel={getEntityActionLabel('edit', 'Test Run')}
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          onEdit={
            testRun
              ? () => {
                  onEdit(testRun);
                }
              : undefined
          }
          onDelete={
            testRun
              ? () => {
                  onDelete(testRun);
                }
              : undefined
          }
          disableEdit={
            testRun ? editRowPendingId === testRun.id || deleteRowPendingId === testRun.id : true
          }
          disableDelete={testRun ? deleteRowPendingId === testRun.id : true}
        />
      </TableCell>
    </TableRow>
  );
};
